/**
 * [BLURY] | 🥭 NoiseBuffer
 * Generates the grain tile. Building it is the expensive part of a frame, so it
 * is cached and only rebuilt when a parameter it actually depends on changes.
 * @build 13.09.26
 * @updated 13.09.26 PHT
 * @author TONYTONY Sàrl
 */
import type { Composition, Size } from './types';

export class NoiseBuffer {
  private canvas = document.createElement('canvas');
  private ctx: CanvasRenderingContext2D;
  private key = '';

  constructor() {
    const ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('NoiseBuffer: 2d context unavailable');
    this.ctx = ctx;
  }

  /** Drop the cache — call after anything that should re-roll the grain. */
  invalidate(): void {
    this.key = '';
  }

  get(composition: Composition, size: Size, seed: number): HTMLCanvasElement {
    const key = [
      composition.gsize,
      composition.gtype,
      composition.gdens,
      seed,
      size.width,
      size.height,
    ].join('|');
    if (key === this.key) return this.canvas;
    this.key = key;

    const nw = Math.ceil(size.width / composition.gsize);
    const nh = Math.ceil(size.height / composition.gsize);
    this.canvas.width = nw;
    this.canvas.height = nh;

    const image = this.ctx.createImageData(nw, nh);
    const data = image.data;
    const density = composition.gdens / 100;
    const mono = composition.gtype === 'mono';

    for (let i = 0; i < data.length; i += 4) {
      if (Math.random() > density) {
        // Mid grey is the no-op value for overlay / soft-light.
        data[i] = data[i + 1] = data[i + 2] = 128;
        data[i + 3] = 255;
        continue;
      }
      if (mono) {
        // Triangular distribution — softer, more film-like than flat random.
        const v = ((Math.random() + Math.random()) * 0.5 * 255) | 0;
        data[i] = data[i + 1] = data[i + 2] = v;
      } else {
        data[i] = (Math.random() * 255) | 0;
        data[i + 1] = (Math.random() * 255) | 0;
        data[i + 2] = (Math.random() * 255) | 0;
      }
      data[i + 3] = 255;
    }

    this.ctx.putImageData(image, 0, 0);
    return this.canvas;
  }
}
